import { Button } from 'components/atoms';
import { useTranslation } from 'react-i18next';
import { PiPlus } from 'react-icons/pi';
import { useGetLabelLightList } from 'services/labels/light';
import { useStoreState } from 'providers/StoreStateContext';
import { IBulkAssignVideoContract } from 'services/channel/types/query';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import ContractCard from './ContractCard';

interface IContractSelectionStepProps {
  channelId: string;
  videoContracts: Array<IBulkAssignVideoContract & { labelId?: string; labelName?: string }>;
  onContractsChange: (contracts: Array<IBulkAssignVideoContract & { labelId?: string; labelName?: string }>) => void;
}

const ContractSelectionStep = ({
  videoContracts,
  onContractsChange,
}: IContractSelectionStepProps) => {
  const { t } = useTranslation();
  const labels = useGetLabelLightList();
  const { applicationSettings } = useStoreState();

  const getLabelOptions = (currentIndex: number) => {
    if (applicationSettings?.allowContractsWithSameLabel) {
      return labels.data?.map(label => ({ value: label.id, label: label.labelName })) ?? [];
    }
    
    const selectedLabelIds = videoContracts
      .map((contract, index) => index !== currentIndex ? contract.labelId : null)
      .filter((id): id is string => !!id);
    
    return labels.data
      ?.filter(label => !selectedLabelIds.includes(label.id))
      .map(label => ({ value: label.id, label: label.labelName })) ?? [];
  };

  const getDivisionPercentageSum = () =>
    videoContracts
      .map(contract => contract.divisionPercentage ?? 0)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0) ?? 0;

  const getContractsWithoutDivision = () =>
    videoContracts.filter(
      ({ divisionPercentage }) =>
        divisionPercentage === null || divisionPercentage === undefined
    ).length || 1;

  const getPercentageLeft = () => {
    const sum = getDivisionPercentageSum();
    const without = getContractsWithoutDivision();
    return sum < 100
      ? parseFloat(((100 - sum) / without).toFixed(2))
      : 0;
  };

  const onAddContract = () => {
    onContractsChange([
      ...videoContracts,
      {
        contractId: '',
        divisionPercentage: 0,
        startDate: '',
        labelId: '',
        labelName: '',
      },
    ]);
  };

  const onRemoveContract = (index: number) => {
    onContractsChange(videoContracts.filter((_, key) => key !== index));
  };

  const onUpdateContract = (
    index: number,
    updates: Partial<IBulkAssignVideoContract & { labelId?: string; labelName?: string }>
  ) => {
    const updated = [...videoContracts];
    updated[index] = { ...updated[index], ...updates };
    onContractsChange(updated);
  };

  return (
    <div className='flex flex-col gap-4 w-full'>
      <div className='flex justify-between items-center'>
        <h3 className='text-lg font-medium'>
          {t('channel.bulkAssign.contracts.title')}
        </h3>
        <Button
          type='button'
          isClear
          onClick={onAddContract}
          variant={BUTTON_VARIANT.submit}>
          <Button.Icon icon={<PiPlus />} />
          <Button.Text text={t('button.add')} />
        </Button>
      </div>

      <div className='flex flex-col gap-4 max-h-96 overflow-y-auto'>
        {videoContracts.map((contract, index) => (
            <ContractCard
              key={index}
              contract={contract}
              index={index}
              labelOptions={getLabelOptions(index)}
              labelsLoading={labels.isLoading}
              percentageLeft={getPercentageLeft()}
              onUpdate={(updates) => onUpdateContract(index, updates)}
              onRemove={() => onRemoveContract(index)}
            />
        ))}
      </div>

      {videoContracts.length === 0 && (
        <div className='text-center text-gray-500 py-8'>
          {t('channel.bulkAssign.contracts.noContracts')}
        </div>
      )}
    </div>
  );
};

export default ContractSelectionStep;

